'use client';

import RichTextList from 'components/shared/RichTextList/RichTextList';
import { Colorways } from 'types/constants';
import { HTMLAttributes } from 'react';
import { cn, getColorway } from 'utils/styles';
import { Cta, Sys, DocumentWithEmbeddedEntries } from 'libs/contentful/types';
import { ContentfulRichTextBlock } from 'libs/contentful/types';
import Button from '../Buttons/BaseButton';
import RichTextCopy from '../RichTextCopy/RichTextCopy';
import { liveUpdateAttrs } from 'libs/contentful/utils';

export interface BaseCardProps {
  color: Colorways;
  children: React.ReactNode;
  className?: string;

  testId?: string;
}

export function BaseCard({ color, className, children, testId, ...props }: BaseCardProps) {
  return (
    <div
      role="group"
      aria-roledescription="slide"
      className={cn(
        'rounded-lg flex flex-col shrink-0 justify-between',
        'min-w-xs',
        'sm:min-w-[425px]',
        getColorway(color),
        className,
      )}
      data-testid={`${testId ?? 'base-card'}-wrapper`}
      {...props}>
      {children}
    </div>
  );
}

interface BaseCardBoxProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  testId?: string;
}

export function BaseCardBox({ className, testId, ...props }: BaseCardBoxProps) {
  return (
    <div
      className={cn(`flex flex-col mb-8 mx-6 mt-6 sm:m-12`, className)}
      data-testid={`${testId ?? 'base-card'}-box`}
      {...props}
    />
  );
}

export interface BaseCardContentProps {
  sys?: Sys;
  cardHeadlineRte: ContentfulRichTextBlock;
  cardCopy: DocumentWithEmbeddedEntries;
  jumpToCta?: Cta;
  testId?: string;
}

export function BaseCardContent({ sys, cardHeadlineRte, cardCopy, jumpToCta, testId }: BaseCardContentProps) {
  const testFixture = testId ?? 'base-card-content';

  return (
    <div data-testid={`${testFixture}-wrapper`}>
      {cardHeadlineRte && (
        <h3
          className="text-heading-md mb-4 font-sans font-semibold"
          data-testid={`${testFixture}-header`}
          {...liveUpdateAttrs(sys?.id, 'cardHeadlineRte')}>
          <RichTextCopy copy={cardHeadlineRte} />
        </h3>
      )}
      <div
        className="font-serif text-body-serif"
        {...liveUpdateAttrs(sys?.id, 'cardCopy')}
        data-testid={`${testFixture}-body`}>
        <RichTextList item={cardCopy} />
      </div>
      {jumpToCta && (
        <div className="mt-8 mb-8 lg:w-[135px]" data-testid={`${testFixture}-button-wrapper`}>
          <Button
            href={jumpToCta.url}
            variant="outline"
            sys={jumpToCta?.sys}
            internalPage={jumpToCta?.internalPage}
            target={jumpToCta?.target}
            data-testid={`${testFixture}-button`}>
            {jumpToCta.text}
          </Button>
        </div>
      )}
    </div>
  );
}
